import React, { Suspense } from 'react';
import { useRoutes, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ProfileButton } from '@/components/layout/profile-button';
import routes from 'tempo-routes';
import { ProtectedLayout } from '@/components/layout/protected-layout';
import { ProfileProvider } from '@/lib/providers';
import { AdminPreviewProvider } from '@/lib/admin-preview-context';
import SamenPage from '@/pages/samen';
import SamenUitnodigingPage from '@/pages/samen-uitnodiging';
import Home from '@/components/home';
import LoginPage from '@/pages/login';
import MeditationCatalog from '@/pages/meditation-catalog';
import MeditationPage from '@/pages/meditation';
import MuziekPage from '@/pages/muziek';
import BinauralBeatsPage from '@/pages/binaural-beats';
import LegalDocumentPage from '@/pages/legal-document';
import SupportPage from '@/pages/support';
import AccountVerwijderenPage from '@/pages/account-verwijderen';
import BinauralBeatPage from '@/pages/binaural-beat';
import FavorietenPage from '@/pages/favorieten';
import NatuurgeluidenPage from '@/pages/natuurgeluiden';
import ProfielPage from '@/pages/profiel';
import TeamPage from '@/pages/team';
import InspiratiekaartenPage from '@/pages/inspiratiekaarten';
import TipsPage from '@/pages/tips';
import AlgemeneTipsPage from '@/pages/algemene-tips';
import ArticlePage from '@/pages/article/id';
import NieuwsPage from '@/pages/nieuws';
import OefeningenPage from '@/pages/oefeningen';
import ChallengePage from '@/pages/challenge';
import SearchPage from '@/pages/search';
import UserResetPasswordPage from '@/pages/reset-password';
import HoofdHartLoginPage from '@/pages/hoofdhart-login';
// Admin imports
import AdminLayout from '@/pages/admin';
import AdminDashboard from '@/pages/admin/dashboard';
import AdminHome from '@/pages/admin/home';
import AdminSettings from '@/pages/admin/settings';
import AdminKeywords from '@/pages/admin/keywords';
import AdminUsers from '@/pages/admin/users';
import AdminTips from '@/pages/admin/tips';
import DeleteUserPage from '@/pages/admin/delete-user';
import AdminAnalytics from '@/pages/admin/analytics';
import AdminQuotes from '@/pages/admin/quotes';
import RunAddSynonyms from '@/pages/admin/run-add-synonyms';
import AdminResetPasswordPage from '@/pages/admin/reset-password';
import ResetPasswordConfirmPage from '@/pages/admin/reset-password-confirm';
import AdminModerationPage from '@/pages/admin/moderatie';
import NotFoundPage from '@/pages/404';
import { ROUTES } from './lib/constants';
import { ToastProvider } from '@/components/ui/toast';
import { Toaster } from '@/components/ui/toaster';
import { DeepLinkListener } from '@/components/deep-link-listener';

import FaqPage from '@/pages/profile/faq';
import ContactPage from '@/pages/profile/contact';
import DocumentsPage from '@/pages/profile/documents';

function App() {
  return (
    <ProfileProvider>
      <AdminPreviewProvider>
      <ToastProvider swipeDirection="right">
        <Toaster />
        <DeepLinkListener />
        <div className="min-h-screen bg-background">
          <ProfileButton />
          <Suspense fallback={<p className="px-[4.75px]">Loading...</p>}>
            {/* For Tempo routes */}
            {import.meta.env.VITE_TEMPO === 'true' && useRoutes(routes)}

            <Routes>
              {/* Public Routes */}
              <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/hoofdhartlogin" element={<HoofdHartLoginPage />} />
              <Route
                path="/reset-password"
                element={<UserResetPasswordPage />}
              />
              <Route
                path="/admin/reset-password-confirm"
                element={<ResetPasswordConfirmPage />}
              />
              {/* Reachable without signing in: both app stores want a
                  privacy policy and a support address in the listing, and a
                  reviewer follows those URLs before anyone has an account. */}
              <Route
                path="/privacy"
                element={
                  <LegalDocumentPage
                    documentName="Privacy Beleid"
                    title="Privacybeleid"
                    description="Welke gegevens de app bewaart, waarvoor ze worden gebruikt en hoe je ze laat verwijderen."
                  />
                }
              />
              <Route
                path="/voorwaarden"
                element={
                  <LegalDocumentPage
                    documentName="Algemene Voorwaarden"
                    title="Algemene voorwaarden"
                    description="De voorwaarden voor het gebruik van de app."
                  />
                }
              />
              <Route path="/support" element={<SupportPage />} />
              {/* Play and the App Store both want a deletion URL that works
                  for someone who can no longer sign in. */}
              <Route
                path="/account-verwijderen"
                element={<AccountVerwijderenPage />}
              />

              {/* The join link in a samen invite mail. Public because the
                  invitee often arrives signed out, and the protected layout
                  would lose the token from the URL. */}
              <Route
                path="/samen/uitnodiging"
                element={<SamenUitnodigingPage />}
              />

              {/* Protected Routes */}
              <Route
                element={
                  <ProtectedLayout>
                    <Outlet />
                  </ProtectedLayout>
                }
              >
                <Route index element={<Home />} />
                <Route
                  path={ROUTES.MEDITATIONS}
                  element={<MeditationCatalog />}
                />
                <Route
                  path={ROUTES.MEDITATIONS + '/:id'}
                  element={<MeditationPage />}
                />
                <Route path={ROUTES.CHALLENGE} element={<ChallengePage />} />
                <Route path="favorieten" element={<FavorietenPage />} />
                <Route
                  path={ROUTES.NATURE_SOUNDS}
                  element={<NatuurgeluidenPage />}
                />
                <Route path={ROUTES.MUSIC} element={<MuziekPage />} />
                <Route path="profiel" element={<ProfielPage />} />
                <Route path="profiel/faq" element={<FaqPage />} />
                <Route path="profiel/contact" element={<ContactPage />} />
                <Route path="profiel/documenten" element={<DocumentsPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="samen" element={<SamenPage />} />
                <Route
                  path={ROUTES.BINAURAL_BEATS}
                  element={<BinauralBeatsPage />}
                />
                <Route
                  path="inspiratiekaarten"
                  element={<InspiratiekaartenPage />}
                />
                <Route
                  path={ROUTES.BINAURAL_BEATS + '/:id'}
                  element={<BinauralBeatPage />}
                />
                <Route path="tips" element={<TipsPage />} />
                <Route path="algemene-tips" element={<AlgemeneTipsPage />} />
                <Route path="article/:id" element={<ArticlePage />} />
                <Route path="oefeningen" element={<OefeningenPage />} />
                <Route path="nieuws" element={<NieuwsPage />} />
                <Route path="search" element={<SearchPage />} />
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="home" element={<AdminHome />} />
                  <Route path="keywords" element={<AdminKeywords />} />
                  <Route path="add-synonyms" element={<RunAddSynonyms />} />
                  <Route
                    path="reset-password"
                    element={<AdminResetPasswordPage />}
                  />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="tips" element={<AdminTips />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="quotes" element={<AdminQuotes />} />
                  <Route path="delete-user" element={<DeleteUserPage />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="moderatie" element={<AdminModerationPage />} />
                  <Route index element={<Navigate to="/admin/dashboard" />} />
                </Route>
              </Route>

              {/* Fallback Route */}
              <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} />} />
            </Routes>
          </Suspense>
        </div>
      </ToastProvider>
      </AdminPreviewProvider>
    </ProfileProvider>
  );
}

export default App;
